<template>
  <BootstrapModal
    ref="modal"
    modalId="addDeviceModal"
    :title="isEdit ? 'Edit Device' : 'Add Device'"
    :closeText="step === 1 ? 'Close' : 'Back'"
    :useCustomClose="true"
    :customCloseEmits="step === 2 ? 'handleStepBack' : 'handleClose'"
    :confirmText="step === 2 ? (isEdit ? 'Save' : 'Add') : 'Next'"
    :extraLeftButton="step === 1 ? {text: 'Save', emit: 'handleDirectDeviceSave'} : null"
    @handleStepBack="handleStepBack"
    @handleClose="handleStepBack"
    @confirm="handleStepConfirm"
    @handleDirectDeviceSave="handleDirectDeviceSave"
  >
    <template #body>
      <form>
        <template v-if="step === 1">
          <div class="mb-3">
            <label class="form-label">Device Name:</label>
            <input
              type="text"
              class="form-control"
              v-model="deviceCopy.name"
              :class="{ 'is-invalid': validationErrors.name }"
              @input="validationErrors.name = false"
            />
          </div>

          <div class="mb-3">
            <label class="form-label">Printer:</label>
            <select class="form-select" v-model="deviceCopy.printer_type">
              <option value="ALL">All</option>
              <option value="DNP">DNP</option>
              <option value="Sony">Sony</option>
            </select>
          </div>

          <div class="mb-3">
            <label class="form-label d-flex justify-content-between">
              <span>Price:</span>
              <span>
                <input
                  type="checkbox"
                  v-model="deviceCopy.active"
                  class="form-check-input me-1"
                  id="activeCheckbox"
                />
                <label for="activeCheckbox" class="form-check-label"
                  >Active</label
                >
              </span>
            </label>
            <input
              type="number"
              step="0.01"
              class="form-control"
              v-model.number="deviceCopy.price"
              :class="{ 'is-invalid': validationErrors.price }"
              @input="validationErrors.price = false"
            />
          </div>

          <div class="mb-3">
            <label class="form-label">Comment:</label>
            <textarea
              class="form-control"
              v-model="deviceCopy.coment"
            ></textarea>
          </div>

          <div class="mb-3">
            <label class="form-label">Auth Code:</label>
            <input
              type="text"
              class="form-control"
              v-model="deviceCopy.auth_code"
              :class="{ 'is-invalid': validationErrors.auth_code }"
              @input="validationErrors.auth_code = false"
            />
          </div>
        </template>

        <template v-else-if="step === 2">
          <div class="input-group mb-3">
            <span class="input-group-text">
              <i class="bi bi-search"></i>
            </span>
            <input
              type="text"
              class="form-control"
              v-model="templateSearch"
              placeholder="Search templates..."
            />
          </div>

          <div style="max-height: 55vh; overflow-y: auto; overflow-x: hidden">
            <ul class="list-group nh">
              <li
                v-for="template in filteredTemplates"
                :key="template.id"
                class="list-group-item list-group-item-action"
                :class="{ active: selectedTemplate?.id === template.id }"
                @click="selectTemplate(template)"
                @dblclick="previewTemplate(template)"
                @contextmenu.prevent="previewTemplate(template)"
                style="cursor: pointer"
              >
                {{ template.name }}
              </li>
            </ul>

            <div
              v-if="previewImageUrl"
              class="image-overlay"
              @click.self="closePreview"
            >
              <button
                class="btn btn-sm btn-close preview-close"
                @click="closePreview"
              ></button>
              <img
                :src="previewImageUrl"
                class="preview-image"
                alt="Template Preview"
              />
            </div>
          </div>

          <div class="mt-3 text-muted small">
            Selected: {{ selectedTemplate?.name || "None" }}
          </div>
        </template>
      </form>
    </template>
  </BootstrapModal>
</template>
  
<script>
import BootstrapModal from "@/components/Common/BootstrapModal.vue";
import DeviceService from "@/services/deviceService";
import emitter from "@/eventBus";

export default {
  name: "DeviceModal",
  components: { BootstrapModal },
  props: {
    templates: Array,
    device: Object,
    mode: {
      type: String,
      default: "add",
    },
  },
  emits: ["device-added", "device-edited"],
  data() {
    return {
      step: 1,
      templateSearch: "",
      selectedTemplate: null,
      previewImageUrl: null,
      deviceCopy: {
        name: "",
        coment: "",
        auth_code: "",
        price: null,
        printer_type: "ALL",
        template_id: null,
        active: true,
      },
      validationErrors: {
        name: false,
        price: false,
        auth_code: false,
      },
    };
  },
  computed: {
    filteredTemplates() {
      const term = this.templateSearch.toLowerCase();
      return this.templates.filter((t) => t.name.toLowerCase().includes(term));
    },
    isEdit() {
      return this.mode === "edit";
    },
  },
  watch: {
    device: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val) {
          this.$nextTick(() => {
            if (this.mode === "edit") {
              this.deviceCopy = {
                ...val,
                price: val.price?.default_unit
                  ? val.price.default_unit / 10
                  : 0,
                active: !!val.active,
              };
              this.selectedTemplate = this.templates.find(
                (t) => t.id === val.template_id
              );
              this.step = 1;
            } else {
              this.resetForm();
            }

            this.$refs.modal.show();
          });
        }
      },
    },
  },

  methods: {
    show() {
      if (this.isEdit && this.device) {
        this.deviceCopy = {
          ...this.device,
          price: this.device.price?.default_unit
            ? this.device.price.default_unit / 10
            : 0,
          active: !!this.device.active,
        };
        this.selectedTemplate = this.templates.find(
          (t) => t.id === this.device.template_id
        );
      } else {
        this.resetForm();
      }

      this.$refs.modal.show();
    },
    handleStepBack() {
      if (this.step === 2) {
        this.step = 1;
      } else {
        this.$refs.modal.hide();
        this.resetForm();
      }
    },
    handleStepConfirm() {
      const { name, price, auth_code } = this.deviceCopy;

      if (this.step === 1) {
        this.validationErrors = {
          name: !name,
          price: price === null || price === undefined || price < 0 ,
          auth_code: !auth_code,
        };

        if (Object.values(this.validationErrors).some(Boolean)) return;
        this.step = 2;
        return;
      }

      if (this.step === 2 && this.selectedTemplate) {
        this.deviceCopy.template_id = this.selectedTemplate.id;

        if (this.isEdit) {
          this.handleEditDevice(); // don't reset step yet
        } else {
          this.step = 1;
          this.handleAddDevice();
        }
      }
    },
    async handleAddDevice() {
      try {
        await DeviceService.addDevice(this.deviceCopy);
        emitter.emit("show-toast", {
          message: "Device added successfully.",
          type: "success",
        });
        this.$emit("device-added");
        this.$refs.modal.hide();
        this.resetForm();
      } catch (error) {
        console.error("Error adding device:", error);
      }
    },
    handleDirectDeviceSave() {
      const { name, price, auth_code } = this.deviceCopy;
      this.validationErrors = {
          name: !name,
          price: price === null || price === undefined || price <= 0,
          auth_code: !auth_code,
        };

      if (Object.values(this.validationErrors).some(Boolean)) return;
      this.handleEditDevice();
    },
    async handleEditDevice() {
      try {
        await DeviceService.updateDevice(this.deviceCopy.id, this.deviceCopy);
        emitter.emit("show-toast", {
          message: "Device updated successfully.",
          type: "success",
        });
        this.$emit("device-edited");
        this.$refs.modal.hide();
        this.resetForm();
      } catch (error) {
        console.error("Error updating device:", error);
      }
    },
    selectTemplate(template) {
      this.selectedTemplate = template;
      this.deviceCopy.template_id = template.id;
    },
    previewTemplate(template) {
      const baseUrl = process.env.VUE_APP_TEMPLATE_THUMBNAIL_URL;
      this.previewImageUrl = `${baseUrl}${template.img_screen}`;
    },
    closePreview() {
      this.previewImageUrl = null;
    },
    resetForm() {
      this.deviceCopy = {
        name: "",
        coment: "",
        auth_code: "",
        price: null,
        printer_type: "ALL",
        template_id: null,
        active: true,
      };
      this.templateSearch = "";
      this.selectedTemplate = null;
      this.previewImageUrl = null;
      this.step = 1;
      this.validationErrors = {
        name: false,
        price: false,
        auth_code: false,
      };
    },
  },
};
</script>
  
  <style scoped>
.image-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1060;
}
.preview-image {
  max-width: 90%;
  max-height: 90%;
  border-radius: 10px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
}
.preview-close {
  position: absolute;
  top: 20px;
  right: 30px;
  color: white;
  background: transparent;
  border: none;
  font-size: 1.5rem;
  z-index: 1061;
}
</style>
  